import { take, select, call, put } from "redux-saga/effects";

import * as heroSelectors from "../Selectors/heroSelectors";
import { HERO_ACTIONS, heroActions } from "../Actions/heroActions";
import { logsActions } from "../Actions/logsActions";
import { HERO_LEVELS } from "../../Classes/utils/constants";
import { gameDataActions } from "../Actions/gameDataActions";
import { COMMAND_BOX_FLAG } from "../../Components/CommandBox";
import { animateActions } from "../Actions/animateActions";
import { MAPS, CITY_DATA } from "../../Helpers/maps";
import * as mapSelectors from "../Selectors/mapSelectors";
import { getMonsters, getHeroName, getHeroPosition, getHeroXP, getHeroLevel, getMapZoneByHeroPosition, getHeroInventory } from "../Selectors/selectors";
import fightSaga, { watchFightSaga, watchMovement, FIGHT_SAGA_RESULTS } from "./fightSaga";
import { ITEMS_LIST } from '../../Classes/utils/constants';
import { startEvent } from "./sagaHelpers";
import citySaga from "./citySaga";
import { all } from 'redux-saga/effects';

// Helpers
import { EVENTS, GAME_STATE, ZONE_TYPES } from "../../Helpers/constants";
import { QUESTS, QUEST_TRIGGER } from "../../Helpers/quests";
import { questsActions } from "../Actions/questsActions";
import { log } from "../../Helpers/logger";

// Match a trigger against the active quest's current step. If it matches,
// advance the step (or complete the quest if the step has no `next`) and
// grant the rewards on completion.
function* progressQuests(triggerType, payload = {}) {
    const quests = yield select(state => state.quests);
    const activeId = quests?.active;
    if (!activeId) return;
    const def = QUESTS[activeId];
    const progress = quests.progress?.[activeId];
    if (!def || !progress) return;
    const step = def.steps[progress.currentStep];
    if (!step || step.trigger?.type !== triggerType) return;

    // Per-trigger gating
    if (triggerType === QUEST_TRIGGER.ENTER_ZONE && step.trigger.zoneType && step.trigger.zoneType !== payload.zoneType) return;
    if (triggerType === QUEST_TRIGGER.HERO_MOVE && step.trigger.count) {
        const counterKey = 'moves';
        yield put(questsActions.incrementCounter(activeId, counterKey, 1));
        const next = (progress.counters[counterKey] || 0) + 1;
        if (next < step.trigger.count) return;
    }

    if (step.next) {
        yield put(questsActions.advanceStep(activeId, step.next));
        yield put(logsActions.add(`Écho de mémoire — ${def.steps[step.next].description}`));
    } else {
        yield put(questsActions.completeQuest(activeId));
        yield put(logsActions.add(`Quête achevée : ${def.title}`));
        if (def.rewards?.xp) yield put(heroActions.gainXp(def.rewards.xp));
        if (def.rewards?.gold) yield put(heroActions.updateGold(def.rewards.gold));
    }
}

const MAP_EVENTS_ENABLED = true;

// Root saga qui combine tous les watchers
function* rootSaga() {
    yield all([
        gameSaga(),
        watchFightSaga(),
        watchMovement()
    ]);
}

function* gameSaga() {
    let overideAction = null;
    while (true) {
    log.saga.log('overideAction', overideAction)
        let heroAction = null;
        if (!overideAction) {
            heroAction = yield take(
                Object.values(HERO_ACTIONS).map((action) => action),
            );
        } else {
            heroAction = { ...overideAction };
            overideAction = null;
        }
        
        // let heroAction = yield take(HERO_ACTIONS.MOVE);

        // log.saga.log('gameSaga', heroAction);
        let state = yield select();
        // log.saga.log(state);
        const heroName = yield select(getHeroName);

        switch (heroAction.type) {
            case HERO_ACTIONS.MOVE: {
                const { x, y } = heroAction.payload;

                let heroPos = yield select(getHeroPosition);
                let xDirection = null;
                if (x > 0) {
                    xDirection = 'right';
                } else if (x < 0) {
                    xDirection = 'left';
                }

                let yDirection = null;
                if (y > 0) {
                    yDirection = 'down';
                } else if (y < 0) {
                    yDirection = 'up';
                }

                let movementLog = null;
                if (xDirection) {
                    if (xDirection === 'right') {
                        movementLog = heroName +' se déplace vers l\'est';
                    } else {
                        movementLog = heroName +' se déplace vers l\'ouest';
                    }
                } else {
                    if (yDirection === 'down') {
                        movementLog = heroName +' se déplace vers le sud';
                    } else {
                        movementLog = heroName +' se déplace vers le nord';
                    }
                }

                // Le déplacement est déjà géré par le composant CommandBox
                // yield put(heroActions.move(x, y)); a été supprimé car il causait un déplacement double
                
                // Vérifier la zone actuelle
                heroPos = yield select(getHeroPosition);
                const currentZone = yield select(getMapZoneByHeroPosition);
                log.saga.log("gameSaga hero move -> ", currentZone?.name ?? `(no zone at ${heroPos.x},${heroPos.y})`);

                const getZoneName = yield call(mapSelectors.getZoneName, state.hero.position);

                // If we're outside any defined zone, skip event/fight/city rolls.
                if (movementLog && currentZone) {
                    yield call(progressQuests, QUEST_TRIGGER.HERO_MOVE);
                    yield call(progressQuests, QUEST_TRIGGER.ENTER_ZONE, { zoneType: currentZone.zoneType });
                    if (!currentZone.safeZone) {
                        yield put(logsActions.add(movementLog));
                        yield put(logsActions.add("Se trouve dans " + getZoneName));

                        // Determiner si evenement aléatoire
                        let eventProbability = yield call(Math.random);
                        eventProbability *= 100;

                        if (MAP_EVENTS_ENABLED && eventProbability > 90) { // 10% de chance d'avoir un événement avec 90
                            yield call(startEvent, EVENTS.event2);
                        } else {
                            // calcule de propabilité de tomber sur un fight avec log qui affiche
                            let fightProbability = yield call(Math.random);
                            fightProbability *= 100;
                            const zoneFightThreshold = yield call(mapSelectors.getZoneFightThreshold, state.hero.position);
                            
                            if (fightProbability > zoneFightThreshold) {
                                yield put(logsActions.add(heroName +' est entré en combat.'));
                                
                                const monsterIds = yield call(mapSelectors.getZoneMonsterIdList, state.hero.position);
                                // log.saga.log(monsterIds)
                                const monsters = yield select(getMonsters);
                                // log.saga.log(monsters);
                                yield put(gameDataActions.generateFightData({
                                    monsterIds, monsters
                                }));
                                
                                yield put(gameDataActions.setToFightMode());

                                // Pass a sentinel action — fightSaga line ~85 guards against missing action.
                                const fightSagaResult = yield call(fightSaga, { type: 'FIGHT_LOOP' });
                                log.saga.log("fightSagaResult::", fightSagaResult)
                                switch (fightSagaResult) {
                                    case FIGHT_SAGA_RESULTS.PLAYER_DEAD: {
                                        yield put(gameDataActions.setGameState(GAME_STATE.GAME_OVER));
                                        yield put(gameDataActions.setCommandBoxFlag(COMMAND_BOX_FLAG.GAME_OVER));
                                        yield put(logsActions.add("C'est ainsi que s'arrêtent les aventures de " + heroName));
                                        break;
                                    }
                                    case FIGHT_SAGA_RESULTS.PLAYER_ESCAPE: {
                                        yield put(gameDataActions.setToNormalMode());
                                        yield put(logsActions.add(heroName + " à réussi à s'enfuir !"));
                                        break;
                                    }
                                    case FIGHT_SAGA_RESULTS.PLAYER_VICTORY: {
                                        const { expDrop, goldDrop } = state.gameData.fightData.endFightData
                                        
                                        state = yield select();
                                        yield put(gameDataActions.setToVictoryMode());
                                        yield put(heroActions.gainXp(expDrop));
                                        yield put(heroActions.updateGold(goldDrop));

                                        const heroXp = yield select(getHeroXP);
                                        const heroLevel = yield select(getHeroLevel);
                                        log.saga.log(heroXp, heroLevel)
                                        if (heroLevel < HERO_LEVELS.length && heroXp >= HERO_LEVELS[heroLevel].xp) {
                                            const statsReducerPayload = [
                                                ['maxHealth', HERO_LEVELS[heroLevel].maxHealth],
                                                ['maxMana', HERO_LEVELS[heroLevel].maxMana]
                                            ];
                                            const nextLevel = heroLevel + 1;
                                            
                                            yield put(heroActions.levelUp(nextLevel, statsReducerPayload));
                                            
                                            yield put(logsActions.add(<b> {heroName + " est passé au niveau " + nextLevel} </b>))
                                        }

                                        yield put(logsActions.add(heroName + " est sortie vainqueur de ce rude combat !"));
                                        yield call(progressQuests, QUEST_TRIGGER.FIGHT_VICTORY);
                                        break;
                                    }
                                        
                                    default:
                                        break;
                                }
                            }
                        }
                    } else {
                        switch (currentZone.zoneType) {
                            case ZONE_TYPES.CITY: {
                                
                                let nextCityData = {
                                    name: getZoneName,
                                    ...CITY_DATA[currentZone.id]
                                }
                                
                                yield put( gameDataActions.setCityData(nextCityData) )
                                yield put(logsActions.add(heroName + " est entré dans la ville de " + getZoneName));

                                overideAction = yield call(citySaga);

                                // log.saga.log(nextCityData);
                                break;
                            }
                            default:
                                break;
                        }
                    }
                    
                }
                break;
            }
            case HERO_ACTIONS.GAIN_XP: {
                const heroXp = yield call(heroSelectors.getXp, state.hero);
                const heroLevel = yield call(heroSelectors.getLevel, state.hero);

                if (heroLevel < HERO_LEVELS.length && heroXp >= HERO_LEVELS[heroLevel].xp) {
                    const statsReducerPayload = [
                        ['maxHealth', HERO_LEVELS[heroLevel].maxHealth],
                        ['maxMana', HERO_LEVELS[heroLevel].maxMana]
                    ];
                    const nextLevel = heroLevel + 1;
                    
                    yield put(heroActions.levelUp(nextLevel, statsReducerPayload));
                    
                    yield put(logsActions.add(heroName + " est passé au niveau " + nextLevel));
                }
                break;
            }
            case HERO_ACTIONS.TAKE_DAMAGE: {
                const health = yield call(heroSelectors.getHealth, state.hero);
                yield put(animateActions.switchHeroStatsDisplayPlayerImgDamage());
                
                if (health === 0) {
                    yield put(gameDataActions.setGameState(GAME_STATE.GAME_OVER));
                    yield put(gameDataActions.setCommandBoxFlag(COMMAND_BOX_FLAG.GAME_OVER));
                    yield put(logsActions.add("C'est ainsi que s'arrêtent les aventures de " + heroName));
                }
                break;
            }
            case HERO_ACTIONS.BUY_ITEM: {
                log.saga.log('gameSaga::', heroAction.payload);
                const { idItem, quantityItem, price } = heroAction.payload;
                const inventory = yield select(getHeroInventory);
                
                let slotId;
                let slotIdOfNull;

                let quantityItemInt = parseInt(quantityItem);

                Object.values(inventory).forEach((slot, index) => {
                    if (slot) {
                        log.saga.log("il y a un slot", slot);
                        if (slot.item.id  === idItem) {
                            log.saga.log("test1", slot);
                            slotId = "slot" + (index+1);
                            // yield put(heroActions.addItemQuantity(slot.id, quantityItem));
                        } else {
                            log.saga.log("non");
                        }
                    } else if (!slotIdOfNull) {
                        log.saga.log("slot vide ");
                        slotIdOfNull = "slot" + (index+1);
                    }
                })

                if (slotId) {
                    yield put(heroActions.buyItemQuantity(slotId, quantityItemInt, price));  
                    yield put(logsActions.add(heroName + " achète " + quantityItemInt + " " + ITEMS_LIST[idItem].name + " pour " + price*-1 + " 📀"));
                } else {
                    yield put(heroActions.buyItemQuantity(slotIdOfNull, quantityItemInt, price, idItem)); 
                    yield put(logsActions.add(heroName + " achète " + quantityItemInt + " " + ITEMS_LIST[idItem].name + " pour " + price*-1 + " 📀"));
                }
                break;
            }
            default:
                break;
        }
    }
}

// Exports pour l'application
export { rootSaga };

// Export par défaut pour la rétrocompatibilité
export default gameSaga;